import assetsBase from '@exodus/assets-base'
import { create } from 'superstruct'
import { createPaymentMethodValidator } from '../payment-method.js'
import { Quote } from '../schema/quote.js'
import { BuyOrder } from '../schema/order.js'
import { PROVIDERS } from '../constants.js'
import {
  DEFAULT_FIAT_PRECISION,
  ENABLED_ASSETS,
  ORDER_STATUSES,
  PAYMENT_METHODS,
} from './constants.js'

const assets = assetsBase.default || assetsBase

const fromExodusToProviderAsset = (asset, assets_ = ENABLED_ASSETS) =>
  Object.fromEntries(Object.entries(assets_).map((entry) => entry.reverse()))[asset]

const fromProviderToExodusAsset = (asset, assets_ = ENABLED_ASSETS) =>
  assets[assets_[`${asset.token.toLowerCase()}_${asset.network.toLowerCase()}`]]

const fromExodusToProviderPaymentMethod = (paymentMethod, parameters = {}) => {
  const providerPaymentMethod = [...PAYMENT_METHODS.values()]
    .flat()
    .find(createPaymentMethodValidator({ ...parameters, type: paymentMethod }))

  return providerPaymentMethod?.id
}

const fromProviderToExodusPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) {
    throw new Error('Payment method is required')
  }

  const type = PAYMENT_METHODS.get(paymentMethod)?.type
  return Array.isArray(type) ? type[0] : type
}

const fromProviderToExodusOrder = (order = {}, assets_ = ENABLED_ASSETS) => {
  const asset = order.data
    ? fromProviderToExodusAsset(
        {
          network: order.data.network,
          token: order.data.assetType,
        },
        assets_
      )
    : undefined

  const networkFee = Number(order.processingFees?.networkFee) || undefined
  const fee = Number(order.processingFees?.transactionFee) || undefined
  const totalFee = networkFee + fee || undefined

  return create(
    {
      createdAt: new Date(order.createdAt),
      extraFee: 0,
      fee,
      from: {
        amount: order.total,
        asset: order.fiatCurrency,
      },
      id: order.id,
      networkFee,
      paymentMethod: order.payment?.paymentMethodType,
      paymentMethodType: order.payment?.paymentMethodType
        ? fromProviderToExodusPaymentMethod(order.payment?.paymentMethodType)
        : undefined,
      provider: PROVIDERS.sardine,
      providerId: order.id,
      // Sardine price is FIAT/CRYPTO only in Complete status
      // It is USD/CRYPTO in other statuses
      rate: order.status === ORDER_STATUSES.complete ? order.data?.price : undefined,
      status: order.status,
      to: {
        amount: order.data?.quantity,
        asset: asset?.name,
      },
      totalFee,
      txId: order.withdrawal?.txHash,
      updatedAt: new Date(order.createdAt),
      walletAddress: order.withdrawal?.walletAddress,
    },
    BuyOrder
  )
}

const fromProviderToExodusQuote = (quote) => {
  const totalFeeFiat = Number(quote.transactionFee + quote.networkFee) || undefined
  const fiatAmount = quote.subtotal
  const cryptoAmount = quote.quantity
  const totalFeeCrypto = Number.isNaN(Number(totalFeeFiat))
    ? totalFeeFiat
    : (cryptoAmount * totalFeeFiat) / fiatAmount

  const toMaxBuyAmount = Number.isNaN(Number(quote.price))
    ? quote.toMaxBuyAmount
    : quote.fromMaxBuyAmount / quote.price

  const toMinBuyAmount = Number.isNaN(Number(quote.price))
    ? quote.toMaxBuyAmount
    : quote.fromMinBuyAmount / quote.price

  return create(
    {
      extraFee: 0,
      fee: quote.transactionFee,
      from: {
        amount: quote.subtotal,
        asset: quote.fromAsset,
        maxBuyAmount:
          quote.fromMaxBuyAmount && quote.maxTransactionValue
            ? Math.min(quote.fromMaxBuyAmount, quote.maxTransactionValue)
            : quote.fromMaxBuyAmount,
        maxSellAmount: quote.fromMaxSellAmount,
        minBuyAmount: quote.fromMinBuyAmount,
        minSellAmount: quote.fromMinSellAmount,
        precision: DEFAULT_FIAT_PRECISION,
      },
      networkFee: quote.networkFee,
      paymentMethod: quote.paymentMethod,
      provider: PROVIDERS.sardine,
      rate: quote.price,
      to: {
        amount: quote.quantity,
        asset: quote.toAsset,
        maxBuyAmount: toMaxBuyAmount,
        maxSellAmount: quote.toMaxSellAmount,
        minBuyAmount: toMinBuyAmount,
        minSellAmount: quote.toMinSellAmount,
        precision: quote.cryptoAssetPrecision,
      },
      totalAmount: quote.total,
      totalFee: totalFeeFiat,
      totalFeeCrypto,
      type: quote.type,
    },
    Quote
  )
}

const sardineAdapter = {
  fromExodusToProviderAsset,
  fromExodusToProviderPaymentMethod,
  fromProviderToExodusAsset,
  fromProviderToExodusPaymentMethod,
  fromProviderToExodusOrder,
  fromProviderToExodusQuote,
}

export default sardineAdapter
